// src/encounter/lib/to-vaccination-requests.ts
// Pre-encounter VACCINATION ADMINISTRATION REQUESTS mapper (PROD-1250).
//
// Port of Flutter `apData.vaccination()` (patient_assessment_model.dart:557) — the source
// for `vaccinationAdministrationRequest()` in pre_encounter_widget.dart:1434. Vaccinations are
// nested under each Dx (assessmentPlanCondition[].childMedicalCode.assessmentPlanVaccination[]),
// NOT top-level (patientAssessmentToCanvas drops them, so the host reads the RAW PatientAssessment
// here). Flutter behaviour reproduced:
//   • flatten every nested vaccination across all conditions
//   • DEDUPE by title (vaccine display) — the same vaccine under >1 Dx collapses to ONE row, and
//     every parent Dx id is accumulated (Flutter's localDiagnosis[]).
//   • NO isAdministrationRequested filter — that flag only switches the row action (Give vs Edit).
//
// Deliberate divergence from Flutter (correctness, matches this repo's soft-delete convention):
// soft-deleted vaccinations (isDeleted === true) are EXCLUDED — Flutter's literal getter doesn't
// filter them, but showing a deleted administration would be a defect. PURE — no I/O.
import type { AssessmentCoding, PatientAssessment } from '@/types/assessment-api';

export interface VaccineAdministrationRequestView {
  /** Stable React key + handle for opening the administration editor (PROD-1227 seam).
   *  The first occurrence's identifier, or a synthesized fallback when none. */
  id: string;
  /** Vaccine display name (the Flutter `title`). '' when the coding has no display. */
  title: string;
  /** When the administration was requested (createdDate, no-Z ISO) or null. */
  requestedOn: string | null;
  /** Requesting clinician's display name (performerDetails.name) or null. */
  requestedBy: string | null;
  /** true → nurse should administer ("Give"); false → doctor editing the request ("Edit"). */
  isAdministrationRequested: boolean;
  /** The vaccine coding (DDC) — carried for the PROD-1227 editor-open + re-save. */
  vaccine: AssessmentCoding | null;
  /** Every parent Dx id this vaccine attaches to (dedupe accumulates across conditions). */
  parentDxIds: string[];
}

/** Read `performerDetails.name` defensively (the wire field is loosely typed Record). */
function performerName(pd: Record<string, unknown> | null | undefined): string | null {
  const name = pd?.name;
  return typeof name === 'string' && name.trim() !== '' ? name : null;
}

export function toVaccinationRequests(
  pa: PatientAssessment | null | undefined,
): VaccineAdministrationRequestView[] {
  const conditions = pa?.assessmentPlanCondition;
  if (!conditions || conditions.length === 0) return [];

  const out: VaccineAdministrationRequestView[] = [];
  // Dedupe by title (vaccine display) — index into `out` so we can append parent Dx ids.
  const byTitle = new Map<string, VaccineAdministrationRequestView>();

  for (const dx of conditions) {
    const dxId = dx.identifier;
    const vaccinations = dx.childMedicalCode?.assessmentPlanVaccination ?? [];
    for (const v of vaccinations) {
      // DELIBERATE divergence from Flutter apData.vaccination() (which does NOT filter) — exclude
      // soft-deleted rows; showing a deleted administration is a defect. Do NOT "fix" back to parity.
      if (v.isDeleted === true) continue;
      const title = v.vaccine?.display ?? '';
      // Dedupe ONLY by a NON-empty title (Flutter keys on title too). An empty/untitled
      // vaccination is degenerate (a real DDC vaccine always has a display); never collapse two
      // distinct untitled rows onto the '' key — that would silently drop one (Codex INFO).
      const existing = title ? byTitle.get(title) : undefined;
      if (existing) {
        if (dxId && !existing.parentDxIds.includes(dxId)) existing.parentDxIds.push(dxId);
        continue;
      }
      const view: VaccineAdministrationRequestView = {
        id: v.identifier ?? `vax-${title}-${out.length}`,
        title,
        requestedOn: v.createdDate ?? null,
        requestedBy: performerName(v.performerDetails),
        isAdministrationRequested: v.isAdministrationRequested === true,
        vaccine: v.vaccine,
        parentDxIds: dxId ? [dxId] : [],
      };
      out.push(view);
      if (title) byTitle.set(title, view);
    }
  }

  return out;
}
